import { query } from '@trlogic/client-js';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { ApiVariable } from '../domain/enrichment/EnrichmentDomain';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import _ from 'lodash';
import Query = query.Query;
import { changeApiVariableDirty } from '../redux/actions/ApiVariableActions';
import DeployErrorUtil from '../util/DeployErrorUtil';

class ApiVariableService {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);

  public async save(_apiVariable: ApiVariable, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    const apiVariable = _.cloneDeep(_apiVariable);
    if (!this.isVariableValid(apiVariable)) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.fillallfields', defaultMessage: 'Please Fill All Fields' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.fillallfields', defaultMessage: 'Please fill all fields' })}</div>,
      });
      return false;
    }
    const exist = (await this.exist(apiVariable.name)) || false;
    if (exist && !isUpdate) {
      MessageUtil.alreadyExist(`${apiVariable.name} already exist. ${exist.type}:${exist.id} is using`);
      return false;
    }
    try {
      const dto = { id: apiVariable.name, object: apiVariable };
      await this.enrichmentClient.save(dto);
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.ENRICHMENT, folder.id, apiVariable.name);
      }
      MessageUtil.success({
        summary: 'API Variable',
        detail: <div>{apiVariable.name} saved.</div>,
      });
      dispatch(changeApiVariableDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: 'API Variable',
        detail: (
          <div>
            <b>{apiVariable.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      console.error(e);
      return false;
    }
  }

  public async delete(id: string) {
    try {
      const exist = await this.exist(id);
      if (exist) {
        const response: any = await this.enrichmentClient.remove(id);
        if (response.code) {
          return false;
        }
        MessageUtil.success({
          summary: 'Variable deleted',
          detail: <div></div>,
        });
        return true;
      } else {
        MessageUtil.error({
          summary: 'Variable can not deleted',
          detail: <div></div>,
        });
      }
    } catch (e: any) {
      if (DeployErrorUtil.show(e, 'Api Variable')) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.API_VARIABLE));
      }
      return false;
    }
  }

  public async deploy(apiVariable: ApiVariable) {
    try {
      await this.enrichmentClient.deploy(apiVariable.name);
      MessageUtil.success({
        summary: 'Api Variable',
        detail: <div>Variable deployed</div>,
      });
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, 'Api Variable')) {
        return;
      }
      MessageUtil.error({
        summary: 'Api Variable',
        detail: <div>Variable can not deployed</div>,
      });
      console.error(error);
    }
  }

  public async undeploy(id: string) {
    try {
      const exist = await this.exist(id);
      if (exist) {
        await this.enrichmentClient.undeploy(id);
        MessageUtil.success({
          summary: 'Api Variable',
          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: `undeployed` })}
            </div>
          ),
        });
      }
    } catch (e) {
      if (DeployErrorUtil.show(e, 'Api Variable')) {
        return;
      }
      MessageUtil.error({
        summary: 'Variable can not undeployed',
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotUndeploy', defaultMessage: `can not undeploy` })}
          </div>
        ),
      });
      console.error(e);
    }
  }

  public async exist(id: string) {
    try {
      if (id != undefined && id.trim().length > 0) {
        return this.enrichmentClient.exists(id);
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  public async count(query: Query) {
    try {
      return this.enrichmentClient.count(query);
    } catch (e) {
      console.log(e);
    }
  }

  private isVariableValid(apiVariable: ApiVariable) {
    return (
      apiVariable.responseType != undefined &&
      apiVariable.responseType.trim().length > 0 &&
      apiVariable.transaction != undefined &&
      apiVariable.transaction.trim().length > 0 &&
      apiVariable.method != undefined &&
      apiVariable.method.trim().length > 0 &&
      apiVariable.name != undefined &&
      apiVariable.name.trim().length > 0 &&
      apiVariable.url != undefined &&
      apiVariable.url.trim().length > 0
    );
  }
}

export default ApiVariableService;
